// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable } from 'rxjs';

// --- 3rd Party
import * as CookieConsent from 'vanilla-cookieconsent';

// --- HS Code
import {
  CONSENT_DEFAULT_LANGUAGE,
  CONSENT_TRANSLATIONS,
} from './consent-translations';

// #endregion

declare let gtag: Function;

/**
 * Consent categories used by the cookie banner. `necessary` is always enabled and
 * covers essential processing (logins, session, and the Intercom support chat, all
 * disclosed in the cookie policy). `analytics` is the only category the user can
 * opt in/out of, gating Google Analytics.
 */
export enum ConsentCategory {
  Necessary = 'necessary',
  Analytics = 'analytics',
}

/**
 * Owns the cookie consent banner (vanilla-cookieconsent).
 *
 * Per the agreed policy for the survey applications, the ONLY non-essential tool is
 * Google Analytics. Everything else — logins/session and the Intercom support chat —
 * is classified as strictly-necessary processing and disclosed in the cookie policy,
 * so it loads unconditionally (Intercom auto-boots from index.html).
 *
 * Responsibilities:
 *  - Renders the consent banner + preferences modal (Necessary + Analytics).
 *  - Bridges "analytics" consent to Google Consent Mode (gtag consent update). GA is
 *    loaded with `analytics_storage: 'denied'` by default (index.html) and only
 *    granted here after the user accepts the Analytics category.
 */
@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  private _initialized = false;

  private readonly _analyticsConsent$ = new BehaviorSubject<boolean>(false);
  /** Emits whether the user has granted the Analytics category. */
  get analyticsConsent$(): Observable<boolean> {
    return this._analyticsConsent$;
  }

  /**
   * Initialise and render the consent banner. Safe to call once (subsequent calls
   * are ignored). `locale` may be a full locale ('zh-CN') or short code ('de');
   * it falls back through short code to English if not translated.
   */
  init(locale?: string): void {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    CookieConsent.run({
      guiOptions: {
        consentModal: { layout: 'box', position: 'bottom right' },
        preferencesModal: { layout: 'box' },
      },
      categories: {
        [ConsentCategory.Necessary]: {
          enabled: true,
          readOnly: true,
        },
        [ConsentCategory.Analytics]: {
          // Google Analytics. Cleared cookies on revoke.
          autoClear: {
            cookies: [{ name: /^_ga/ }, { name: '_gid' }, { name: '_gat' }],
          },
        },
      },
      language: {
        default: this._resolveLanguage(locale),
        translations: CONSENT_TRANSLATIONS,
      },
      onFirstConsent: () => this._applyConsent(),
      onConsent: () => this._applyConsent(),
      onChange: () => this._applyConsent(),
    });
  }

  /** Re-open the preferences modal (e.g. from a "Cookie settings" link). */
  showPreferences(): void {
    CookieConsent.showPreferences();
  }

  /**
   * Switch the banner language to match the app's current language. Accepts a full
   * locale ('zh-CN') or short code ('de'); falls back through short code to English.
   */
  setLanguage(locale: string): void {
    CookieConsent.setLanguage(this._resolveLanguage(locale));
  }

  /**
   * Reflect the user's current Analytics choice into Google Consent Mode.
   * Called on first consent, on load with existing consent, and on any change.
   */
  private _applyConsent(): void {
    const analytics = CookieConsent.acceptedCategory(ConsentCategory.Analytics);

    gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
    });
    this._analyticsConsent$.next(analytics);
  }

  /**
   * Resolve the CookieConsent language to use. Accepts either a full locale
   * (e.g. 'zh-CN') or a short language code (e.g. 'de'). Tries the full locale
   * first, then the short code, then falls back to English.
   */
  private _resolveLanguage(locale?: string): string {
    if (!locale) {
      return CONSENT_DEFAULT_LANGUAGE;
    }
    if (CONSENT_TRANSLATIONS[locale]) {
      return locale;
    }
    const shortCode = locale.split('-')[0];
    if (CONSENT_TRANSLATIONS[shortCode]) {
      return shortCode;
    }
    return CONSENT_DEFAULT_LANGUAGE;
  }
}
